import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import {
  Search, Loader2, CheckCircle2, AlertTriangle, MapPin, Info,
  LocateFixed, Save, Home, Building2, Store, Star, Trash2, X,
} from "lucide-react";

// Fix leaflet default marker icon (webpack/CRA can't find the images by default)
const DEFAULT_MARKER_ICON = L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const LABEL_ICONS = {
  home: Home,
  office: Building2,
  shop: Store,
  work: Building2,
};
const labelIconFor = (label) => LABEL_ICONS[(label || "").toLowerCase()] || Star;

/**
 * Google Places Autocomplete → interactive draggable-pin map → free-text detail field.
 *
 * Three input methods (all resolve to the same final pin coords used for distance):
 *  1. Search with Places Autocomplete → pick a suggestion → adjust the pin.
 *  2. "Use my current location" → browser geolocation → adjust the pin. (opt-in)
 *  3. If autocomplete finds nothing, pick the nearest landmark then drag the pin.
 *
 * Optional features (props):
 *  - allowCurrentLocation: show the "Use my current location" button (pickup + trip origin).
 *  - allowSavedAddresses:  show the saved-address quick-picks + "Save this address" button
 *                          (requires an authenticated user; silently no-op if unauth).
 *
 * value shape: { ...LocationPoint, formatted_address, details }
 */
const AddressForm = ({ label, value, onChange, allowCurrentLocation = false, allowSavedAddresses = false }) => {
  const [q, setQ] = useState(value?.address || "");
  const [details, setDetails] = useState(value?.details || "");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const [pinBusy, setPinBusy] = useState(false);
  const [pinNote, setPinNote] = useState("");
  // Geolocation state
  const [geoState, setGeoState] = useState({ loading: false, error: "", accuracyM: null });
  // Saved addresses
  const [savedList, setSavedList] = useState([]);
  const [savedLoaded, setSavedLoaded] = useState(false);
  const [showSaveBox, setShowSaveBox] = useState(false);
  const [saveLabel, setSaveLabel] = useState("");
  const [saving, setSaving] = useState(false);

  const debounceRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const mapEl = useRef(null);
  const accuracyCircleRef = useRef(null);

  const point = value;
  const testidBase = label.toLowerCase().replace(/\s+/g, "-");
  const authed = !!(typeof window !== "undefined" && localStorage.getItem("cit_token"));

  // Keep details field in sync so parent gets both address + details
  const emit = (pt, det) => {
    if (!pt) { onChange(null); return; }
    onChange({ ...pt, details: det ?? details });
  };

  useEffect(() => { emit(point, details); /* eslint-disable-next-line */ }, [details]);

  // Load saved addresses once (only for authenticated users when the feature is enabled)
  useEffect(() => {
    if (!allowSavedAddresses || !authed || savedLoaded) return;
    api.get("/saved-addresses")
      .then((r) => setSavedList(r.data || []))
      .catch(() => setSavedList([]))
      .finally(() => setSavedLoaded(true));
  }, [allowSavedAddresses, authed, savedLoaded]);

  // Autocomplete lookup
  useEffect(() => {
    if (q && q.length >= 2 && q !== point?.address && q !== point?.formatted_address) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        try {
          setLoading(true); setError(""); setNoResults(false);
          const r = await api.get(`/geo/autocomplete?q=${encodeURIComponent(q)}`);
          const items = r.data || [];
          setSuggestions(items);
          setOpen(true);
          setNoResults(items.length === 0);
        } catch (e) {
          setError("Couldn't fetch address suggestions. Try again.");
          setSuggestions([]);
          setNoResults(false);
        } finally { setLoading(false); }
      }, 350);
    } else {
      setSuggestions([]);
      setNoResults(false);
    }
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line
  }, [q]);

  const pick = async (s) => {
    setOpen(false); setSuggestions([]); setError(""); setNoResults(false);
    // If suggestion already has an inline point (geocoding fallback), use it directly
    if (s.point) {
      const pt = { ...s.point, place_id: s.place_id, formatted_address: s.point.address || s.description };
      setQ(pt.address || s.description);
      setPinNote("Drag the pin to your exact spot if this isn't quite right.");
      emit(pt, details);
      return;
    }
    try {
      setLoading(true);
      const r = await api.get(`/geo/place?place_id=${encodeURIComponent(s.place_id)}`);
      const pt = { ...r.data, formatted_address: r.data.address };
      setQ(pt.address);
      setPinNote("Drag the pin to your exact spot if this isn't quite right.");
      emit(pt, details);
    } catch (e) {
      setError("Couldn't resolve that address. Please pick another suggestion.");
    } finally { setLoading(false); }
  };

  // Handle marker drag / map click: reverse-geocode + update the point (preserving formatted_address)
  const handleDragEnd = async () => {
    if (!markerRef.current || !point) return;
    const { lat, lng } = markerRef.current.getLatLng();
    setPinBusy(true); setError("");
    // Clear GPS accuracy circle once the user starts adjusting manually
    if (accuracyCircleRef.current) {
      try { accuracyCircleRef.current.remove(); } catch (_) {}
      accuracyCircleRef.current = null;
      setGeoState((g) => ({ ...g, accuracyM: null }));
    }
    try {
      const r = await api.get(`/geo/reverse?lat=${lat}&lng=${lng}`);
      const updated = {
        ...r.data,
        formatted_address: point.formatted_address || point.address,
        place_id: point.place_id,
        lat, lng,
      };
      setQ(updated.address);
      setPinNote("Pin updated to your dragged position.");
      emit(updated, details);
    } catch (e) {
      const updated = { ...point, lat, lng };
      setPinNote("Pin position saved. (Couldn't fetch a fresh address for it — we'll use the coordinates.)");
      emit(updated, details);
    } finally {
      setPinBusy(false);
    }
  };

  // "Use my current location"
  const useCurrentLocation = () => {
    if (!("geolocation" in navigator)) {
      setGeoState({ loading: false, error: "Your browser does not support location. Please search or drop a pin instead.", accuracyM: null });
      return;
    }
    setGeoState({ loading: true, error: "", accuracyM: null });
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng, accuracy } = pos.coords;
        try {
          const r = await api.get(`/geo/reverse?lat=${lat}&lng=${lng}`);
          const pt = {
            ...r.data,
            lat, lng,
            formatted_address: r.data.address,
          };
          setQ(pt.address);
          setPinNote(accuracy && accuracy > 100
            ? "GPS accuracy is approximate — please drag the pin to your exact spot."
            : "We placed the pin at your current location. Drag it if it isn't quite right.");
          setGeoState({ loading: false, error: "", accuracyM: accuracy || null });
          emit(pt, details);
        } catch (e) {
          // Reverse geocode failed — still allow using the coords with a synthetic address
          const pt = {
            address: `Near ${lat.toFixed(5)}, ${lng.toFixed(5)}`,
            area: "", city: "", pincode: "",
            lat, lng,
            formatted_address: `Near ${lat.toFixed(5)}, ${lng.toFixed(5)}`,
          };
          setQ(pt.address);
          setPinNote("We captured your GPS coordinates but couldn't fetch a readable address. Please drag the pin to confirm the spot.");
          setGeoState({ loading: false, error: "", accuracyM: accuracy || null });
          emit(pt, details);
        }
      },
      (err) => {
        let msg = "Couldn't fetch your location.";
        if (err.code === 1) msg = "Location permission denied. You can still search or drop a pin below.";
        else if (err.code === 2) msg = "Your location is unavailable right now. You can still search or drop a pin.";
        else if (err.code === 3) msg = "Location request timed out. Try again, or search instead.";
        setGeoState({ loading: false, error: msg, accuracyM: null });
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 },
    );
  };

  // Pick a saved address in one tap
  const applySaved = (s) => {
    const pt = { ...s.point };
    setQ(pt.address || pt.formatted_address || "");
    setDetails(s.details || "");
    setPinNote(`Loaded "${s.label}". Drag the pin if you need to adjust.`);
    emit(pt, s.details || "");
  };

  const removeSaved = async (e, id) => {
    e.stopPropagation();
    try {
      await api.delete(`/saved-addresses/${id}`);
      setSavedList((xs) => xs.filter((x) => x.id !== id));
    } catch (_) { /* ignore */ }
  };

  // Save the current pin as a labelled address
  const saveCurrent = async () => {
    if (!point) return;
    const lbl = (saveLabel || "").trim();
    if (!lbl) return;
    setSaving(true);
    try {
      const r = await api.post("/saved-addresses", { label: lbl, point, details });
      // Upsert into local list (replace by id or push)
      setSavedList((xs) => {
        const idx = xs.findIndex((x) => x.id === r.data.id);
        if (idx >= 0) { const copy = [...xs]; copy[idx] = r.data; return copy; }
        return [r.data, ...xs];
      });
      setShowSaveBox(false); setSaveLabel("");
      setPinNote(`Saved as "${r.data.label}".`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't save this address.");
    } finally { setSaving(false); }
  };

  // Initialise / update the interactive map
  useEffect(() => {
    if (!mapEl.current) return;
    if (!point) {
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; markerRef.current = null; accuracyCircleRef.current = null; }
      return;
    }
    if (!mapRef.current) {
      const m = L.map(mapEl.current, {
        center: [point.lat, point.lng], zoom: 16,
        zoomControl: true, scrollWheelZoom: true,
      });
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; OpenStreetMap',
      }).addTo(m);
      const marker = L.marker([point.lat, point.lng], { draggable: true, icon: DEFAULT_MARKER_ICON, autoPan: true }).addTo(m);
      marker.on("dragend", handleDragEnd);
      m.on("click", (e) => {
        marker.setLatLng(e.latlng);
        handleDragEnd();
      });
      markerRef.current = marker;
      mapRef.current = m;
    } else {
      mapRef.current.setView([point.lat, point.lng], mapRef.current.getZoom() || 16);
      if (markerRef.current) markerRef.current.setLatLng([point.lat, point.lng]);
    }

    // Draw / update GPS accuracy circle if we have one
    if (accuracyCircleRef.current) {
      try { accuracyCircleRef.current.remove(); } catch (_) {}
      accuracyCircleRef.current = null;
    }
    if (geoState.accuracyM && geoState.accuracyM > 30) {
      accuracyCircleRef.current = L.circle([point.lat, point.lng], {
        radius: geoState.accuracyM,
        color: "#D38138", weight: 1, fillColor: "#D38138", fillOpacity: 0.12,
      }).addTo(mapRef.current);
    }

    const t = setTimeout(() => { try { mapRef.current && mapRef.current.invalidateSize(); } catch (_) {} }, 60);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [point?.lat, point?.lng, geoState.accuracyM]);

  const showSavedRow = allowSavedAddresses && authed && savedList.length > 0;
  const canSaveNow = allowSavedAddresses && authed && !!point;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
        {allowCurrentLocation && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            data-testid={`${testidBase}-use-current-location`}
            onClick={useCurrentLocation}
            disabled={geoState.loading}
            className="h-7 rounded-full text-[11px] gap-1 px-2 border-amber-200 text-amber-800 hover:bg-amber-50"
          >
            {geoState.loading
              ? <><Loader2 className="w-3 h-3 animate-spin"/> Locating…</>
              : <><LocateFixed className="w-3 h-3"/> Use current location</>}
          </Button>
        )}
      </div>

      {/* Saved-address quick picks */}
      {showSavedRow && (
        <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`${testidBase}-saved-list`}>
          {savedList.map((s) => {
            const Icon = labelIconFor(s.label);
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => applySaved(s)}
                data-testid={`${testidBase}-saved-chip-${s.label.toLowerCase()}`}
                className="group inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 hover:bg-amber-50 hover:border-amber-300 px-2.5 py-1 text-[11px] text-slate-700"
                title={s.point?.address || s.label}
              >
                <Icon className="w-3 h-3 text-amber-700"/>
                <span className="font-medium">{s.label}</span>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => removeSaved(e, s.id)}
                  className="ml-1 opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-600"
                  aria-label={`Delete ${s.label}`}
                >
                  <Trash2 className="w-3 h-3"/>
                </span>
              </button>
            );
          })}
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
        <Input
          data-testid={`${testidBase}-autocomplete-input`}
          value={q}
          onChange={(e) => { setQ(e.target.value); if (point) onChange(null); }}
          onFocus={() => { if (suggestions.length) setOpen(true); }}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          placeholder="Start typing an address, area, or landmark"
          className="rounded-xl pl-9"
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin"/>}
        {open && suggestions.length > 0 && (
          <div className="absolute z-50 mt-1 w-full rounded-xl bg-white border border-slate-200 shadow-lg overflow-hidden" data-testid={`${testidBase}-suggestions`}>
            {suggestions.map((s, i) => (
              <button
                key={s.place_id || i}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); pick(s); }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-amber-50 border-b border-slate-100 last:border-none"
              >{s.description}</button>
            ))}
          </div>
        )}
      </div>

      {/* Status line */}
      <div className="mt-2 text-xs">
        {error && <div className="text-rose-700 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3"/> {error}</div>}
        {!error && geoState.error && (
          <div className="text-amber-700 inline-flex items-start gap-1" data-testid={`${testidBase}-geo-error`}>
            <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0"/> <span>{geoState.error}</span>
          </div>
        )}
        {!error && !geoState.error && !point && !loading && !noResults && (
          <div className="text-slate-400">Search, use your current location{allowCurrentLocation ? "" : ""}, or drop a pin — final coordinates are what get used.</div>
        )}
        {!error && !point && noResults && (
          <div className="text-amber-700 inline-flex items-start gap-1" data-testid={`${testidBase}-no-results`}>
            <Info className="w-3 h-3 mt-0.5 shrink-0"/>
            <span>No exact match. Try a nearby landmark, area, or the closest main road — then drag the pin on the map to your exact spot.</span>
          </div>
        )}
        {point && !error && (
          <div className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3"/> Selected · {point.city || point.area || "location captured"}</div>
        )}
      </div>

      {point && (
        <>
          <div className="mt-3">
            <label className="text-[10px] uppercase tracking-widest text-slate-500">House / flat no., floor, landmark <span className="text-rose-500">*</span></label>
            <Input
              data-testid={`${testidBase}-details-input`}
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="e.g., Flat 302, Tower B, near community park"
              className="rounded-xl mt-1"
            />
          </div>

          <div className="mt-3 flex items-center justify-between gap-2">
            <div className="text-[11px] text-slate-500 inline-flex items-center gap-1" data-testid={`${testidBase}-pin-hint`}>
              <MapPin className="w-3 h-3 text-amber-600"/>
              Drag the pin (or tap the map) to the exact spot. Distance uses the pin's position.
            </div>
            {pinBusy && <Loader2 className="w-3 h-3 animate-spin text-slate-400"/>}
          </div>
          <div
            ref={mapEl}
            className="mt-2 h-56 rounded-xl border border-slate-100 overflow-hidden"
            data-testid={`${testidBase}-adjust-map`}
          />
          {pinNote && (
            <div className="mt-2 text-[11px] text-slate-500" data-testid={`${testidBase}-pin-note`}>{pinNote}</div>
          )}
          {geoState.accuracyM && geoState.accuracyM > 100 && (
            <div className="mt-1 text-[11px] text-amber-700 inline-flex items-center gap-1" data-testid={`${testidBase}-accuracy-warning`}>
              <AlertTriangle className="w-3 h-3"/> GPS approximate to ~{Math.round(geoState.accuracyM)} m — drag the pin to correct it.
            </div>
          )}
          <div className="mt-1 text-[10px] text-slate-400" data-testid={`${testidBase}-coords`}>
            Pin: {point.lat.toFixed(5)}, {point.lng.toFixed(5)}
            {point.formatted_address && point.formatted_address !== point.address && (
              <span className="ml-2">· Searched: {point.formatted_address}</span>
            )}
          </div>

          {/* Save this address */}
          {canSaveNow && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              {!showSaveBox ? (
                <button
                  type="button"
                  onClick={() => setShowSaveBox(true)}
                  data-testid={`${testidBase}-save-address-btn`}
                  className="text-[11px] text-amber-700 hover:text-amber-900 inline-flex items-center gap-1 font-medium"
                >
                  <Save className="w-3 h-3"/> Save this address for one-tap next time
                </button>
              ) : (
                <div className="flex items-center gap-2" data-testid={`${testidBase}-save-address-box`}>
                  <div className="flex flex-wrap gap-1">
                    {["Home", "Office", "Shop"].map((preset) => (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => setSaveLabel(preset)}
                        className={`text-[11px] px-2 py-1 rounded-full border ${saveLabel === preset ? "bg-amber-100 border-amber-300 text-amber-900" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                      >{preset}</button>
                    ))}
                  </div>
                  <Input
                    value={saveLabel}
                    onChange={(e) => setSaveLabel(e.target.value)}
                    placeholder="Label"
                    className="rounded-lg h-8 text-xs w-24"
                    data-testid={`${testidBase}-save-label-input`}
                    maxLength={40}
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={saveCurrent}
                    disabled={saving || !saveLabel.trim()}
                    className="h-8 rounded-full bg-amber-600 hover:bg-amber-700 text-[11px]"
                    data-testid={`${testidBase}-save-confirm-btn`}
                  >{saving ? <Loader2 className="w-3 h-3 animate-spin"/> : "Save"}</Button>
                  <button
                    type="button"
                    onClick={() => { setShowSaveBox(false); setSaveLabel(""); }}
                    className="text-slate-400 hover:text-slate-700"
                    aria-label="Cancel"
                  ><X className="w-4 h-4"/></button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AddressForm;
